import React, { useEffect, useState } from 'react';
import { ShoppingBag, ArrowRight, ExternalLink, ZoomIn, X } from 'lucide-react';
// Products are populated from the TikTok Shop (@chancellorandceo).
// Each product links to the TikTok Shop. Swap `link` with direct product URLs when available.
const products = [
  {
    id: 'spend-responsibly-red-label',
    name: 'Spend Responsibly — Red Label Tee',
    description: 'Heavyweight unisex tee with the signature red-boxed "$PEND RESPONSIBLY" chest stamp. A quiet flex — for the ones who know every dollar is a decision.',
    image: 'https://customer-assets.emergentagent.com/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/l4m3b5jw_19ec7250e38.jpg',
    link: 'https://www.fanbasis.com/agency-checkout/Formalizeai/kOBNv'
  },
  {
    id: 'spend-responsibly-outside',
    name: 'Spend Responsibly — Outside Tee',
    description: 'The everyday unisex soft-touch tee. Clean white canvas, stacked red "$PEND RESPONSIBLY" mark on the chest. Built for the days you move different.',
    image: 'https://customer-assets.emergentagent.com/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/5epbfla5_Person%203%20Context%2C%20White.jpg',
    link: 'https://www.fanbasis.com/agency-checkout/Formalizeai/nN7jp'
  },
  {
    id: 'stacked-spend-responsibly-hoody',
    name: 'Stacked Spend Responsibly Hoody',
    description: 'Premium unisex pullover hoodie in clean white with the stacked red "$PEND RE$PON SIBLY." emblem at the chest. Heavyweight feel, drawstring hood, kangaroo pocket — the uniform of disciplined wealth builders.',
    image: 'https://customer-assets.emergentagent.com/job_f39e6fee-79f2-4684-89ed-32979a04e68f/artifacts/ma8l4mxm_stacked-spend-responsibly-hooded-sweatshirt.jpg',
    link: 'https://www.fanbasis.com/agency-checkout/Formalizeai/pXBly'
  }
];

const TIKTOK_SHOP_URL = 'https://www.tiktok.com/@chancellorandceo';

const MerchPage = () => {
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    if (!zoomedImage) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setZoomedImage(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomedImage]);

  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="tradelines-hero" data-testid="merch-hero">
        <div className="tradelines-hero-content">
          <div className="tradelines-badge">THE COLLINS MERCH</div>
          <h1 className="display-huge tradelines-title" style={{ fontSize: '42px' }}>
            <span className="text-gradient">Wear the Blueprint. Live the Legacy.</span>
          </h1>
          <div
            style={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '14px 28px',
              marginTop: '8px',
              color: 'var(--accent-gold)',
              fontSize: '13px',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              fontWeight: 600
            }}
            data-testid="merch-hero-strip"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingBag size={16} /> Limited Drop
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ArrowRight size={16} /> Ships Nationwide
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <ArrowRight size={16} /> Unisex Fit · Premium Cotton
            </span>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section style={{ padding: '80px 7.6923%', background: 'var(--bg-secondary)' }} data-testid="merch-products-section">
        {products.length === 0 ? (
          <div
            style={{
              maxWidth: '720px',
              margin: '0 auto',
              textAlign: 'center',
              background: 'rgba(35, 15, 61, 0.6)',
              border: '1px solid rgba(212, 175, 55, 0.35)',
              borderRadius: '16px',
              padding: '60px 30px'
            }}
            data-testid="merch-coming-soon"
          >
            <ShoppingBag size={56} style={{ color: 'var(--accent-gold)', marginBottom: '20px' }} />
            <h2 style={{ color: 'white', fontSize: '28px', marginBottom: '14px' }}>
              The Drop Is Loading…
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '17px', lineHeight: '1.7', marginBottom: '28px' }}>
              We&apos;re staging the official Chancellor merch — apparel, accessories, and
              limited-edition pieces. In the meantime, browse the live collection on TikTok Shop.
            </p>
            <a
              href={TIKTOK_SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ fontSize: '17px', padding: '16px 32px' }}
              data-testid="merch-empty-shop-cta"
            >
              Visit TikTok Shop <ExternalLink size={18} />
            </a>
          </div>
        ) : (
          <>
            <div className="section-header">
              <h2 className="display-medium section-title">The Collection</h2>
              <p className="body-medium section-description">
                Each piece is a daily reminder: position yourself, access capital, build legacy.
              </p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '32px',
                maxWidth: '1300px',
                margin: '40px auto 0'
              }}
            >
              {products.map((product) => (
                <div
                  key={product.id}
                  data-testid={`merch-card-${product.id}`}
                  style={{
                    background: 'rgba(35, 15, 61, 0.6)',
                    border: '1px solid rgba(138, 93, 255, 0.3)',
                    borderRadius: '14px',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-out, border-color 0.3s ease-out'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setZoomedImage(product)}
                    aria-label={`Zoom ${product.name}`}
                    data-testid={`merch-zoom-${product.id}`}
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      background: '#0c0418',
                      marginBottom: '20px',
                      padding: 0,
                      border: 'none',
                      cursor: 'zoom-in',
                      position: 'relative',
                      display: 'block'
                    }}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        background: 'rgba(12, 4, 24, 0.78)',
                        border: '1px solid rgba(212, 175, 55, 0.5)',
                        color: 'var(--accent-gold)',
                        borderRadius: '999px',
                        padding: '6px 10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        letterSpacing: '0.3px'
                      }}
                      aria-hidden="true"
                    >
                      <ZoomIn size={14} /> Zoom
                    </span>
                  </button>
                  <h3 style={{ color: 'white', fontSize: '19px', fontWeight: '700', marginBottom: '8px', lineHeight: '1.35' }}>
                    {product.name}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.7', marginBottom: '22px', flex: '1' }}>
                    {product.description}
                  </p>
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    data-testid={`merch-buy-${product.id}`}
                  >
                    Buy Now <ArrowRight size={18} />
                  </a>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Final Brand Statement */}
      <section className="tradelines-cta-section">
        <div className="tradelines-cta-content">
          <h2 className="display-medium">Repping the Movement</h2>
          <p className="body-large" style={{ marginBottom: '30px', maxWidth: '700px', margin: '0 auto 30px' }}>
            Every shirt and hoodie is a signal — to the world and to yourself — that you&apos;re
            building something bigger than a paycheck. Spend like it matters. Because it does.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '20px',
              maxWidth: '900px',
              margin: '0 auto'
            }}
            data-testid="merch-final-pillars"
          >
            <div
              style={{
                background: 'rgba(35, 15, 61, 0.55)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                borderRadius: '12px',
                padding: '22px 20px'
              }}
            >
              <h3 style={{ color: 'var(--accent-gold)', fontSize: '15px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: '10px' }}>
                Made For Builders
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.65, margin: 0 }}>
                Designed for the founders, closers, and quiet operators who treat every dollar like a decision.
              </p>
            </div>
            <div
              style={{
                background: 'rgba(35, 15, 61, 0.55)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                borderRadius: '12px',
                padding: '22px 20px'
              }}
            >
              <h3 style={{ color: 'var(--accent-gold)', fontSize: '15px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: '10px' }}>
                Premium Build
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.65, margin: 0 }}>
                Heavyweight cotton, embroidered marks, unisex fit. Built to last beyond the trend cycle.
              </p>
            </div>
            <div
              style={{
                background: 'rgba(35, 15, 61, 0.55)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                borderRadius: '12px',
                padding: '22px 20px'
              }}
            >
              <h3 style={{ color: 'var(--accent-gold)', fontSize: '15px', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: '10px' }}>
                Limited Drop
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.65, margin: 0 }}>
                Three pieces. One mission. When the run sells out, the next drop won&apos;t look the same.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox / Zoom Modal */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${zoomedImage.name} — enlarged view`}
          data-testid="merch-lightbox"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(5, 2, 14, 0.94)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            cursor: 'zoom-out',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomedImage(null); }}
            aria-label="Close zoomed image"
            data-testid="merch-lightbox-close"
            style={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              background: 'rgba(12, 4, 24, 0.8)',
              border: '1px solid rgba(212, 175, 55, 0.5)',
              color: 'var(--accent-gold)',
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={22} />
          </button>
          <img
            src={zoomedImage.image}
            alt={zoomedImage.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '92vw',
              maxHeight: '88vh',
              objectFit: 'contain',
              borderRadius: '12px',
              boxShadow: '0 30px 80px rgba(0,0,0,0.7)',
              cursor: 'default'
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              background: 'rgba(12, 4, 24, 0.8)',
              border: '1px solid rgba(138, 93, 255, 0.4)',
              borderRadius: '999px',
              padding: '8px 18px',
              fontSize: '13px',
              letterSpacing: '0.4px',
              textAlign: 'center',
              maxWidth: '90vw'
            }}
          >
            {zoomedImage.name}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchPage;
